import {
    ContaminationDeclarationFormValues,
    TEXT_FIELDS,
    TextField,
    YES_NO_FIELDS,
    YesNoField,
} from "./fields";
import { SubstanceRow } from "./types";

/**
 * Structural view of a declaration as the form needs it. The codegen'd
 * `ContaminationDeclaration` type is assignable to this, but keeping the form
 * independent of `Maybe`/`__typename` keeps the pure utils testable.
 */
export type ContaminationDeclarationSource =
    { [K in TextField]?: string | null } &
    { [K in YesNoField]?: boolean | null } & {
        runningTimeHours?: number | null;
        declarationAccepted?: boolean | null;
        substances?: Array<Partial<Record<keyof SubstanceRow, string | null>> | null> | null;
    };

export const toSubstanceRow = (
    row: Partial<Record<keyof SubstanceRow, string | null>> | null | undefined,
): SubstanceRow => ({
    chemical: row?.chemical ?? '',
    dangerClass: row?.dangerClass ?? '',
    risks: row?.risks ?? '',
    firstAid: row?.firstAid ?? '',
});

/**
 * Builds the Formik initial values from a declaration. Unanswered yes/no
 * questions stay `null` (never coerced to false — "No" carries legal weight),
 * texts become '' and the running time is rendered as plain digits.
 */
export const toFormValues = (
    declaration: ContaminationDeclarationSource | null | undefined,
): ContaminationDeclarationFormValues => {
    const source: ContaminationDeclarationSource = declaration ?? {};
    const values = {} as ContaminationDeclarationFormValues;

    TEXT_FIELDS.forEach((field) => {
        const value = source[field];
        values[field] = typeof value === 'string' ? value : '';
    });
    YES_NO_FIELDS.forEach((field) => {
        const value = source[field];
        values[field] = typeof value === 'boolean' ? value : null;
    });

    values.runningTimeHours = typeof source.runningTimeHours === 'number'
        ? String(source.runningTimeHours)
        : '';
    values.declarationAccepted = source.declarationAccepted === true;
    values.substances = (source.substances ?? []).filter(Boolean).map(toSubstanceRow);

    return values;
};
